<template>
  <v-app :class="{ 'theme--dark': isDarkMode }">
    <HeaderDealerDashboard
      :userName="userName"
      :businessName="businessName"
      :isDarkModeProp="isDarkMode"
      @navigate="handleNavigation"
    />

    <v-main class="dealer-main" :class="{ 'dark-mode': isDarkMode }">
      <v-container fluid class="pa-6">
        <!-- Page Header -->
        <div class="page-header mb-6">
          <h1 class="text-h4 font-weight-bold text-white mb-0" style="background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%); padding: 20px; border-radius: 8px;">
            <span class="material-icons-round mr-3" style="vertical-align: middle;">location_on</span>
            Dealer Portal - Distribution
          </h1>
        </div>

        <!-- Rules Section -->
        <v-card class="mb-6" elevation="3">
          <v-card-title class="text-h5 font-weight-bold primary--text d-flex justify-space-between align-center">
            <div>
              <span class="material-icons-round mr-2">rule</span>
              Rules
            </div>
            <v-btn 
              color="primary" 
              outlined 
              @click="showSaveRulesDialog = true"
              class="text-capitalize"
            >
              <span class="material-icons-round mr-2">save</span>
              Save Rules
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-6">
            <v-form ref="rulesForm" v-model="rulesFormValid">
              <div class="rules-section">
                <!-- Nationwide checkbox -->
                <v-checkbox
                  v-model="rules.nationwide"
                  label="Nationwide (all agents)"
                  class="mb-4"
                ></v-checkbox>

                <!-- ZIP Targeting -->
                <div class="rule-item mb-4">
                  <v-checkbox
                    v-model="rules.zipTargeting.enabled"
                    label="ZIP Targeting"
                    :disabled="rules.nationwide"
                  ></v-checkbox>

                  <div v-if="rules.zipTargeting.enabled && !rules.nationwide" class="mt-3">
                    <v-row>
                      <v-col cols="12" md="8">
                        <v-text-field
                          v-model="newZipCode"
                          label="Add ZIP Code"
                          outlined
                          dense
                          placeholder="Enter 5-digit ZIP code"
                          @keyup.enter="addZipCode"
                          :rules="zipCodeRules"
                          maxlength="5"
                        >
                          <template v-slot:append>
                            <v-btn
                              icon
                              small
                              color="primary"
                              @click="addZipCode"
                              :disabled="!isValidZipCode(newZipCode)"
                            >
                              <span class="material-icons-round">add</span>
                            </v-btn>
                          </template>
                        </v-text-field>
                      </v-col>
                    </v-row>

                    <div v-if="rules.zipTargeting.codes.length > 0" class="zip-codes-list mt-2">
                      <div class="text-subtitle-2 mb-2">Selected ZIP Codes ({{ rules.zipTargeting.codes.length }}):</div>
                      <v-chip
                        v-for="(zip, index) in rules.zipTargeting.codes"
                        :key="index"
                        close
                        @click:close="removeZipCode(index)"
                        class="mr-2 mb-2"
                        color="primary"
                        outlined
                      >
                        {{ zip }}
                      </v-chip>
                    </div>

                    <div v-else class="text-caption text-grey-600 mt-2">
                      No ZIP codes selected. Add ZIP codes to target specific areas.
                    </div>
                  </div>
                </div>

                <!-- Min Agent Rating -->
                <div class="rule-item mb-4">
                  <v-checkbox
                    v-model="rules.minRating.enabled"
                    label="Min Agent Rating"
                    :disabled="rules.nationwide"
                  ></v-checkbox>

                  <div v-if="rules.minRating.enabled && !rules.nationwide" class="mt-3">
                    <v-row align="center">
                      <v-col cols="12" md="8">
                        <v-slider
                          v-model="rules.minRating.value"
                          min="0"
                          max="5"
                          step="0.1"
                          thumb-label="always"
                          track-color="grey lighten-2"
                          color="primary"
                          class="rating-slider"
                        >
                          <template v-slot:prepend>
                            <span class="text-body-2">0★</span>
                          </template>
                          <template v-slot:append>
                            <span class="text-body-2">5★</span>
                          </template>
                          <template v-slot:thumb-label="{ value }">
                            {{ value }}★
                          </template>
                        </v-slider>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model.number="rules.minRating.value"
                          type="number"
                          min="0"
                          max="5"
                          step="0.1"
                          outlined
                          dense
                          suffix="★"
                          label="Rating"
                          @input="validateRating"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    <div class="text-caption text-grey-600">
                      Only agents with rating {{ rules.minRating.value }}★ or higher will receive leads
                    </div>
                  </div>
                </div>

                <!-- Max Agents per ZIP -->
                <div class="rule-item mb-4">
                  <v-checkbox
                    v-model="rules.maxAgentsPerZip.enabled"
                    label="Max Agents per ZIP"
                    :disabled="rules.nationwide"
                  ></v-checkbox>

                  <div v-if="rules.maxAgentsPerZip.enabled && !rules.nationwide" class="mt-3">
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model.number="rules.maxAgentsPerZip.value"
                          label="Maximum Agents per ZIP Code"
                          type="number"
                          min="1"
                          max="100"
                          outlined
                          dense
                          :rules="maxAgentsRules"
                          suffix="agents"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    <div class="text-caption text-grey-600">
                      Limit the number of agents that can receive leads from each ZIP code
                    </div>
                  </div>
                </div>
              </div>
            </v-form>

            <!-- Map Preview Placeholder -->
            <div class="map-preview mt-6">
              <div class="map-placeholder">
                <span class="material-icons-round" style="font-size: 48px; color: #ccc;">map</span>
                <p class="text-center text-grey mt-2">[ Map Preview of Coverage ]</p>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- Caps & Exceptions Section -->
        <v-card elevation="3">
          <v-card-title class="text-h5 font-weight-bold primary--text d-flex justify-space-between align-center">
            <div>
              <span class="material-icons-round mr-2">settings</span>
              Caps & Exceptions
            </div>
            <v-btn 
              color="primary" 
              outlined 
              @click="showManageDialog = true"
              class="text-capitalize"
            >
              <span class="material-icons-round mr-2">manage_accounts</span>
              Manage
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-6">
            <div class="caps-info">
              <div class="cap-item mb-3">
                <strong>Cap:</strong> {{ caps.nationwide }} agents nationwide
              </div>
              <div class="cap-item mb-3">
                <strong>Exceptions:</strong> {{ caps.exceptions }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-container>
    </v-main>

    <FooterDealer />

    <!-- Save Rules Dialog -->
    <v-dialog v-model="showSaveRulesDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6">Save Distribution Rules</v-card-title>
        <v-card-text>
          <p>Are you sure you want to save these distribution rules? This will affect how leads are distributed to agents.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="showSaveRulesDialog = false">Cancel</v-btn>
          <v-btn color="blue" dark @click="saveRules">Save Rules</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Manage Caps Dialog -->
    <v-dialog v-model="showManageDialog" max-width="600">
      <v-card>
        <v-card-title class="text-h6">Manage Caps & Exceptions</v-card-title>
        <v-card-text>
          <v-form ref="capsForm" v-model="capsFormValid">
            <v-text-field
              v-model="caps.nationwide"
              label="Nationwide Agent Cap"
              type="number"
              outlined
              :rules="[validationRules.required, validationRules.positive]"
            ></v-text-field>
            <v-textarea
              v-model="caps.exceptions"
              label="Exceptions (e.g., Priority to 85041 top-rated agents)"
              outlined
              rows="3"
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="showManageDialog = false">Cancel</v-btn>
          <v-btn color="blue" @click="saveCaps" :disabled="!capsFormValid">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import HeaderDealerDashboard from '@/components/Layout/HeaderDealerDashboard.vue'
import FooterDealer from '@/components/Layout/FooterDealer.vue'

export default {
  name: 'DealerDistribution',
  components: {
    HeaderDealerDashboard,
    FooterDealer
  },
  data() {
    return {
      isDarkMode: true,
      loading: false,
      userName: '',
      businessName: '',
      rulesFormValid: false,
      capsFormValid: false,
      showSaveRulesDialog: false,
      showManageDialog: false,
      newZipCode: '',
      rules: {
        nationwide: true,
        zipTargeting: {
          enabled: false,
          codes: []
        },
        minRating: {
          enabled: false,
          value: 4.0
        },
        maxAgentsPerZip: {
          enabled: false,
          value: 10
        }
      },
      caps: {
        nationwide: 250,
        exceptions: 'Priority to 85041 top-rated agents'
      },
      validationRules: {
        required: value => !!value || 'This field is required',
        positive: value => value > 0 || 'Value must be greater than 0'
      },
      zipCodeRules: [
        v => !!v || 'ZIP code is required',
        v => /^\d{5}$/.test(v) || 'ZIP code must be 5 digits',
        v => !this.rules.zipTargeting.codes.includes(v) || 'ZIP code already added'
      ],
      maxAgentsRules: [
        v => !!v || 'Number of agents is required',
        v => v > 0 || 'Must be greater than 0',
        v => v <= 100 || 'Cannot exceed 100 agents'
      ]
    }
  },
  beforeCreate() {
    // Check authentication
    if (!localStorage.getItem('token') || localStorage.getItem('user_flag') !== 'dealer') {
      this.$router.push('/dealers/login')
    }
  },
  async mounted() {
    this.loadDarkModePreference()
    await this.loadUserData()
    await this.loadDistributionData()
  },
  methods: {
    loadDarkModePreference() {
      const saved = localStorage.getItem('dealer_dark_mode')
      this.isDarkMode = saved !== null ? saved === 'true' : true
    },
    loadUserData() {
      this.userName = localStorage.getItem('user_name') || 'Dealer'
      this.businessName = localStorage.getItem('business_name') || 'Business'
    },
    async loadDistributionData() {
      try {
        this.loading = true
        const token = localStorage.getItem('token')
        
        const response = await axios.get('/api/dealer/distribution-data', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.data.flag === 1) {
          this.rules = response.data.data.rules || this.rules
          this.caps = response.data.data.caps || this.caps
        }
      } catch (error) {
        console.error('Error loading distribution data:', error)
        this.$toast.error('Error loading distribution data')
      } finally {
        this.loading = false
      }
    },
    handleNavigation(item) {
      // Navigation is handled by the HeaderDealerDashboard component
    },
    addZipCode() {
      if (this.isValidZipCode(this.newZipCode) && !this.rules.zipTargeting.codes.includes(this.newZipCode)) {
        const zipToAdd = this.newZipCode
        this.rules.zipTargeting.codes.push(zipToAdd)
        this.newZipCode = ''
        this.$toast.success(`ZIP code ${zipToAdd} added successfully`)
      }
    },
    removeZipCode(index) {
      const removedZip = this.rules.zipTargeting.codes[index]
      this.rules.zipTargeting.codes.splice(index, 1)
      this.$toast.info(`ZIP code ${removedZip} removed`)
    },
    isValidZipCode(zipCode) {
      return zipCode && /^\d{5}$/.test(zipCode) && !this.rules.zipTargeting.codes.includes(zipCode)
    },
    validateRating() {
      if (this.rules.minRating.value < 0) {
        this.rules.minRating.value = 0
      } else if (this.rules.minRating.value > 5) {
        this.rules.minRating.value = 5
      }
      // Round to 1 decimal place
      this.rules.minRating.value = Math.round(this.rules.minRating.value * 10) / 10
    },
    async saveRules() {
      try {
        this.loading = true
        const token = localStorage.getItem('token')
        
        const response = await axios.post('/api/dealer/save-distribution-rules', this.rules, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.data.flag === 1) {
          this.$toast.success('Distribution rules saved successfully!')
          this.showSaveRulesDialog = false
        } else {
          this.$toast.error(response.data.message || 'Error saving rules')
        }
      } catch (error) {
        console.error('Error saving distribution rules:', error)
        this.$toast.error('Error saving distribution rules')
      } finally {
        this.loading = false
      }
    },
    async saveCaps() {
      if (!this.$refs.capsForm.validate()) {
        return
      }

      try {
        this.loading = true
        const token = localStorage.getItem('token')
        
        const response = await axios.post('/api/dealer/save-caps', this.caps, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.data.flag === 1) {
          this.$toast.success('Caps & exceptions saved successfully!')
          this.showManageDialog = false
        } else {
          this.$toast.error(response.data.message || 'Error saving caps')
        }
      } catch (error) {
        console.error('Error saving caps:', error)
        this.$toast.error('Error saving caps')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.dealer-main {
  background-color: #f5f5f5;
  min-height: 100vh;
}
.dealer-main.dark-mode {
  background: url('~@/assets/img/skyline-bg-dark.png') no-repeat center bottom;
  background-size: cover;
  background-color: #0f0f23;
}

.page-header h1 {
  text-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.rules-section .rule-item {
  border-left: 3px solid #1976d2;
  padding-left: 16px;
  margin-bottom: 16px;
}

.zip-codes-list {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  background-color: #fafafa;
}

.rating-slider {
  margin-top: 8px;
}

.rating-slider .v-slider__thumb-label {
  background-color: #1976d2 !important;
}

.map-placeholder {
  height: 200px;
  background-color: #f8f9fa;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.caps-info .cap-item {
  font-size: 16px;
  line-height: 1.6;
}

.v-card {
  border-radius: 12px !important;
}

.v-card-title {
  padding: 20px 24px 16px 24px;
}

.v-card-text {
  padding: 0 24px 24px 24px;
}
</style>

<style>
/* Dark mode styles - unscoped for proper cascading */
.dark-mode .v-card {
  background: rgba(30, 30, 60, 0.8) !important;
  border: 1px solid rgba(138, 43, 226, 0.3);
}
/* Text colors */
.dark-mode .v-card-title,
.dark-mode .v-card-text,
.dark-mode .v-card-text *,
.dark-mode .v-list-item__title,
.dark-mode .v-list-item__subtitle,
.dark-mode .v-list-item__content,
.dark-mode .cap-item,
.dark-mode .rule-item,
.dark-mode .text-h4,
.dark-mode .text-h5,
.dark-mode .text-h6,
.dark-mode .text-subtitle-1,
.dark-mode .text-subtitle-2,
.dark-mode .text-body-1,
.dark-mode .text-body-2,
.dark-mode .text-caption,
.dark-mode p,
.dark-mode span:not(.v-btn__content span):not(.v-chip__content),
.dark-mode strong,
.dark-mode label,
.dark-mode .v-label,
.dark-mode .v-messages__message,
.dark-mode .v-input__slot,
.dark-mode .v-text-field input,
.dark-mode .v-textarea textarea,
.dark-mode .v-select__selection,
.dark-mode .v-checkbox .v-label,
.dark-mode .v-radio .v-label,
.dark-mode .v-switch .v-label,
.dark-mode .v-slider__thumb-label {
  color: #e0e0e0 !important;
}
/* Icons */
.dark-mode .v-icon,
.dark-mode .material-icons,
.dark-mode .material-icons-round,
.dark-mode .material-icons-outlined {
  color: #e0e0e0 !important;
}
/* Button icons should keep their color */
.dark-mode .v-btn .v-icon,
.dark-mode .v-btn .material-icons,
.dark-mode .v-btn .material-icons-round {
  color: inherit !important;
}
/* Colored text overrides */
.dark-mode .v-card-title.primary--text {
  color: #42a5f5 !important;
}
.dark-mode .primary--text {
  color: #42a5f5 !important;
}
.dark-mode .text-grey,
.dark-mode .text-grey-600 {
  color: rgba(255,255,255,0.6) !important;
}
/* Component specific */
.dark-mode .zip-codes-list {
  background-color: rgba(30, 30, 60, 0.5);
  border-color: rgba(138, 43, 226, 0.3);
}
.dark-mode .map-placeholder {
  background-color: rgba(30, 30, 60, 0.5);
  border-color: rgba(138, 43, 226, 0.3);
}
</style>
